import { useGetOrganizationReferenceDataAccessQuery } from '@/legacy-api/organizations-slice'

/**
 * Hook to fetch and use organization reference data access permissions
 * This determines which reference data fields (S&P ratings, Moody's ratings, etc.)
 * the user's organization has access to
 */
export const useReferenceDataPermissions = () => {
  const { data, isLoading, error } =
    useGetOrganizationReferenceDataAccessQuery()

  // When loading, return null for permissions to avoid flickering
  // The UI should handle null by showing a neutral state
  if (isLoading) {
    return {
      hasSPAccess: null,
      hasMoodysAccess: null,
      hasFitchAccess: null,
      hasCusipAccess: null,
      hasAnalyticsAccess: null,
      hasLiquidityScoresAccess: null,
      hasRealtimePricingAccess: null,
      hasEquityAccess: null,
      hasFundAccess: null,
      hasBothRatingsAccess: null,
      hasAnyRatingAccess: null,
      hasEquityAndFundAccess: null,
      isLoading: true,
      error,
      data,
    }
  }

  return {
    // Individual field access
    hasSPAccess: data?.sp ?? false,
    hasMoodysAccess: data?.moodys ?? false,
    hasFitchAccess: data?.fitch ?? false,
    hasCusipAccess: data?.cusip ?? false,
    hasAnalyticsAccess: data?.analytics ?? false,
    hasLiquidityScoresAccess: data?.liquidity_scores ?? false,
    hasRealtimePricingAccess: data?.realtime_pricing ?? false,
    hasEquityAccess: data?.equity ?? false,
    hasFundAccess: data?.fund ?? false,

    // Combined access for ratings
    hasBothRatingsAccess: (data?.sp ?? false) && (data?.moodys ?? false),
    hasAnyRatingAccess:
      (data?.sp ?? false) || (data?.moodys ?? false) || (data?.fitch ?? false),

    // Combined access for equity and fund (security-list strategies)
    hasEquityAndFundAccess: (data?.equity ?? false) && (data?.fund ?? false),

    // Loading and error states
    isLoading: false,
    error,

    // Raw data
    data,
  }
}
